import React, { useState } from "react";
import { Button } from "../ui/button";
import { Loader2, Plus, Upload, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { services } from "../../services";
import { useToast } from "../ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";

interface AssetCreationSummary {
  total: number;
  successful: number;
  failed: number;
  skipped: number;
}

interface AssetCreationResponse {
  message: string;
  status: boolean;
  data: any[];
  count: number;
  summary: AssetCreationSummary;
}

interface AssetCreateAutoProps {
  onRefresh?: () => void;
}

export default function AssetCreateAuto({ onRefresh }: AssetCreateAutoProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [showPopUp, setShowPopUp] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [assetCount, setAssetCount] = useState<number>();
  // Static function for uploading CSV file
  const handleCSVUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    // Validate file type
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      toast({
        title: "Invalid File Type",
        description: "Please select a valid CSV file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "File size must be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    // Store the selected file
    setSelectedFile(file);
    setFileName(file.name);
  };

  const handleSubmitCSV = async () => {
    if (!selectedFile) {
      toast({
        title: "No File Selected",
        description: "Please select a CSV file first",
        variant: "destructive",
      });
      return;
    }

    // Set loading state
    setIsLoading(true);

    // Read and process the CSV file
    const reader = new FileReader();
    reader.onload = async (e) => {
      const csvContent = e.target?.result as string;

      // Improved CSV parsing function
      const parseCSV = (csvText: string) => {
        const lines = csvText.split("\n").filter((line) => line.trim());
        if (lines.length === 0) return { headers: [], data: [] };

        // Parse headers
        const headerLine = lines[0];
        const headers = parseCSVLine(headerLine);

        // Parse data rows
        const data = lines.slice(1).map((line) => {
          const values = parseCSVLine(line);
          const row: Record<string, string> = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          return row;
        });

        return { headers, data };
      };

      // Helper function to parse a single CSV line with proper quote handling
      const parseCSVLine = (line: string): string[] => {
        const result: string[] = [];
        let current = "";
        let inQuotes = false;
        let i = 0;

        while (i < line.length) {
          const char = line[i];

          if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
              // Escaped quote
              current += '"';
              i += 2;
            } else {
              // Toggle quote state
              inQuotes = !inQuotes;
              i++;
            }
          } else if (char === "," && !inQuotes) {
            // End of field
            result.push(current.trim());
            current = "";
            i++;
          } else {
            current += char;
            i++;
          }
        }

        // Add the last field
        result.push(current.trim());
        return result;
      };

      const { headers, data } = parseCSV(csvContent);

      // Log RERA and VARA fields specifically for debugging
      if (data.length > 0) {
        const firstRow = data[0];
        const relevantKeys = Object.keys(firstRow).filter(
          (key) =>
            key.toLowerCase().includes("rera") ||
            key.toLowerCase().includes("vara") ||
            key.toLowerCase().includes("document") ||
            key.toLowerCase().includes("approved"),
        );
      }

      const dataToSend = {
        csvData: data,
        assetCount: assetCount,
      };

      try {
        // Send CSV data to the API endpoint
        const response = await services.post(
          "/asset-create-auto/process-assets",
          dataToSend,
        );

        setCsvData(data);

        // Show toast with summary
        const responseData = response.data as AssetCreationResponse;
        if (responseData && responseData.summary) {
          const { summary } = responseData;
          const message = `Asset creation completed! Total: ${summary.total}, Successful: ${summary.successful}, Failed: ${summary.failed}, Skipped: ${summary.skipped}`;

          toast({
            title: responseData.message || "Asset Creation Summary",
            description: message,
            variant: "default",
          });
        } else {
          toast({
            title: "Asset Creation",
            description:
              responseData?.message || "Asset creation process completed",
            variant: "default",
          });
        }

        // Refresh the NFTs table after successful processing
        if (onRefresh) {
          onRefresh();
        }

        // Close the popup after successful processing
        setShowPopUp(false);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to process CSV file. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        // Reset the file input and selected file
        setSelectedFile(null);
        setFileName("");
      }
    };

    reader.onerror = () => {
      toast({
        title: "Error",
        description: "Error reading file",
        variant: "destructive",
      });
      setIsLoading(false);
    };

    reader.readAsText(selectedFile);
  };

  const handleShowPopUp = () => {
    setShowPopUp(true);
  };

  const handleClosePopUp = () => {
    setShowPopUp(false);
    setSelectedFile(null);
    setFileName("");
  };

  return (
    <div className="space-y-4">
      <Button
        className="flex items-center gap-2 bg-gradientButton"
        onClick={handleShowPopUp}
      >
        <Upload className="h-4 w-4" />
        Choose CSV File
      </Button>

      <Dialog open={showPopUp} onOpenChange={setShowPopUp}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload CSV File</DialogTitle>
            <DialogDescription>
              Select a CSV file to automatically create assets. The file should
              contain the required asset information.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center justify-center py-6">
            {!isLoading ? (
              <div className="flex flex-col text-center w-full">
                <Button
                  className="flex items-center gap-2 bg-gradientButton mb-4"
                  onClick={() => document.getElementById("csv-upload")?.click()}
                >
                  <Upload className="h-4 w-4" />
                  Choose CSV File
                </Button>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCSVUpload}
                  className="hidden"
                  id="csv-upload"
                />

                {fileName && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm font-medium text-green-800">
                      Selected File: {fileName}
                    </p>
                  </div>
                )}
                <p className="text-sm text-muted-foreground">
                  Supported format: CSV (max 10MB)
                </p>
                <Input
                  type="number"
                  placeholder="Enter the number of assets to create"
                  className="bg-white mt-4 dark:bg-gray-900 border-gray-200 dark:border-gray-700 focus:ring-primary dark:focus:ring-primary"
                  value={assetCount}
                  onChange={(e) => setAssetCount(Number(e.target.value))}
                />
              </div>
            ) : (
              <div className="text-center">
                <Button className="flex items-center gap-2" disabled>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </Button>
                <p className="text-sm text-muted-foreground mt-2">
                  Please wait while we process your CSV file...
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClosePopUp}
              disabled={isLoading}
            >
              Cancel
            </Button>
            {selectedFile && !isLoading && (
              <Button className="bg-gradientButton" onClick={handleSubmitCSV}>
                Submit & Process
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
